// =============================================================================
// src/features/crisis/crisis-ticker.ts
// Ticker ao vivo: render dos itens, loop sem emenda e botao de pausa.
//
// Proveniencia: porte FIEL de assets/crisis.js 192-289 (tickerItemHTML/
//   buildTicker + toggle). O conteudo troca por estado; a duracao da animacao e
//   proporcional a largura medida (~45 px/s) para velocidade constante,
//   independentemente de quantos itens ha. Com prefers-reduced-motion nao ha
//   auto-scroll (conjunto unico, scroll manual). A medicao so ocorre com o
//   takeover visivel (offsetParent existe), senao scrollWidth=0.
//
// SRP: so o ticker. O conteudo vive em crisis-ticker-feed.ts.
// =============================================================================

import { on, qs } from '../../app/dom';
import type { CrisisTickerView, TickerItem, TickerMetricItem } from './crisis-ticker-feed';
import { tickerFeedFor } from './crisis-ticker-feed';

/** Velocidade do feed em px por segundo — "lentamente" (assets/crisis.js 197). */
const TICKER_PX_PER_SECOND = 45;

/** Duracao minima da animacao em segundos (assets/crisis.js 275). */
const TICKER_MIN_DURATION_S = 20;

/** Icones de play/pause do botao (assets/crisis.js 231-234). */
const TICKER_TOGGLE_ICON = {
  pause: '<i class="ti ti-player-pause-filled crz-ico sm" aria-hidden="true"></i>',
  play: '<i class="ti ti-player-play-filled crz-ico sm" aria-hidden="true"></i>',
} as const;

/**
 * Controla o ticker ao vivo. Uma instancia por takeover; bindToggle() liga o
 * botao de pausa e renderTicker(view) (re)monta o feed do estado pedido.
 */
export class CrisisTickerController {
  constructor(private readonly reduceMotion: boolean) {}

  /** Liga o botao pausar/retomar (assets/crisis.js 282-289). */
  bindToggle(): void {
    const ticker = qs<HTMLElement>('#crzTicker');
    const toggle = qs<HTMLElement>('#crzTickerToggle');
    if (!ticker || !toggle) return;
    on(toggle, 'click', () => this.togglePaused(ticker, toggle));
  }

  /** Alterna is-paused + estado acessivel + icone (assets/crisis.js 283-288). */
  private togglePaused(ticker: HTMLElement, toggle: HTMLElement): void {
    const paused = ticker.classList.toggle('is-paused');
    toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
    toggle.setAttribute('aria-label', paused ? 'Retomar feed' : 'Pausar feed');
    toggle.innerHTML = paused ? TICKER_TOGGLE_ICON.play : TICKER_TOGGLE_ICON.pause;
  }

  /**
   * (Re)monta o feed para o estado `view`. No-op se faltar #crzTicker/track
   * (guard de assets/crisis.js 255).
   */
  renderTicker(view: CrisisTickerView): void {
    const ticker = qs<HTMLElement>('#crzTicker');
    const track = qs<HTMLElement>('#crzTickerTrack');
    if (!ticker || !track) return;
    const set = tickerFeedFor(view).map(renderTickerItem).join('');
    ticker.classList.toggle('is-calm', view === 'calm');
    if (this.reduceMotion) {
      this.renderStatic(ticker, track, set);
      return;
    }
    this.renderAnimated(ticker, track, set);
  }

  /** Sem auto-scroll: conjunto unico, scroll manual (assets/crisis.js 260-266). */
  private renderStatic(ticker: HTMLElement, track: HTMLElement, set: string): void {
    track.classList.remove('is-animated');
    track.innerHTML = set;
    ticker.classList.add('is-static');
  }

  /** Loop sem emenda: duplica o conjunto e mede a duracao (assets/crisis.js 268-279). */
  private renderAnimated(ticker: HTMLElement, track: HTMLElement, set: string): void {
    ticker.classList.remove('is-static');
    track.classList.remove('is-animated');
    track.innerHTML = set + set; // duplica -> loop continuo
    if (track.offsetParent === null) return; // invisivel: scrollWidth seria 0
    const oneSet = track.scrollWidth / 2;
    if (oneSet <= 0) return;
    const duration = Math.max(TICKER_MIN_DURATION_S, Math.round(oneSet / TICKER_PX_PER_SECOND));
    ticker.style.setProperty('--crz-ticker-dur', `${duration}s`);
    track.classList.add('is-animated');
  }
}

/** Markup de um item do ticker (assets/crisis.js 236-252). */
function renderTickerItem(item: TickerItem): string {
  if (item.kind === 'metric') return renderMetricItem(item);
  return (
    `<span class="crz-ticker-item">` +
    `<span class="crz-ticker-pip2 ${item.sent}" aria-hidden="true"></span>` +
    `<span class="crz-ticker-src">${item.src}</span>` +
    `<span class="crz-ticker-txt">${item.text}</span>` +
    `<span class="crz-ticker-time">${item.time}</span>` +
    `</span>`
  );
}

/** Markup de uma pilula de metrica, com seta conforme a direcao (assets/crisis.js 237-244). */
function renderMetricItem(item: TickerMetricItem): string {
  return (
    `<span class="crz-ticker-item"><span class="crz-ticker-metric">` +
    `<span class="lbl">${item.label}</span>` +
    `<span class="val ${item.dir}">${metricArrow(item.dir)}${item.value}</span>` +
    `</span></span>`
  );
}

/** Seta de tendencia para a metrica (assets/crisis.js 238-240). */
function metricArrow(dir: TickerMetricItem['dir']): string {
  if (dir === 'up-bad' || dir === 'up-good') {
    return '<i class="ti ti-trending-up crz-ico sm" aria-hidden="true"></i>';
  }
  if (dir === 'down-bad') {
    return '<i class="ti ti-trending-down crz-ico sm" aria-hidden="true"></i>';
  }
  return '';
}
